import React from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styles from '../css-isolated/su_homepage.module.css';
import API_BASE_URL from '../config';
import analyticsService from '../services/analyticsService';
import ChangePasswordModal from './change_pass';
import authFetch from '../utils/authFetch';

function SuHomepage() {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [userData, setUserData] = React.useState(null);
  const [isDarkMode, setIsDarkMode] = React.useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || savedTheme === null;
  });
  const [showThemeModal, setShowThemeModal] = React.useState(false);
  const [pendingTheme, setPendingTheme] = React.useState(null);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  
  // Analytics data state
  const [analyticsData, setAnalyticsData] = React.useState({
    kpis: null,
    demographics: null,
    ageDistribution: null,
    genderDistribution: null,
    activities: null,
    facility: null,
    healthRecordsCount: null,
    dailyConsultationCount: null,
    generalDormitoryCount: null,
    isolationCount: null,
    hospitalCount: null,
    unassignedPdlCount: null,
    cellDistribution: null
  });
  const [loading, setLoading] = React.useState(true);

  // Check authentication on mount
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      navigate('/login-superuser', { replace: true });
      return;
    }
    try {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login-superuser', { replace: true });
    }
  }, [navigate]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load analytics data
  React.useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setLoading(true);
        
        // Load data in parallel for better performance
        const [
          dashboardData, 
          ageData, 
          genderData,
          healthRecordsCount,
          dailyConsultationCount,
          generalDormitoryCount,
          isolationCount,
          hospitalCount,
          unassignedPdlCount,
          cellDistribution
        ] = await Promise.all([
          analyticsService.getDashboardData(),
          analyticsService.getAgeDistribution(),
          analyticsService.getGenderDistribution(),
          analyticsService.getHealthRecordsCount(),
          analyticsService.getDailyConsultationCount(),
          analyticsService.getGeneralDormitoryCount(),
          analyticsService.getIsolationCount(),
          analyticsService.getHospitalCount(),
          analyticsService.getUnassignedPdlCount(),
          analyticsService.getCellDistribution()
        ]);
        setAnalyticsData({
          kpis: dashboardData.kpis,
          demographics: dashboardData.demographics,
          ageDistribution: ageData,
          genderDistribution: genderData,
          activities: dashboardData.activities,
          facility: dashboardData.facility,
          healthRecordsCount,
          dailyConsultationCount,
          generalDormitoryCount,
          isolationCount,
          hospitalCount,
          unassignedPdlCount,
          cellDistribution
        });
      } catch (error) {
        console.error('Failed to load analytics data:', error);
        // Keep default values if API fails
      } finally {
        setLoading(false);
      }
    };

    loadAnalyticsData();
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleDarkMode = (newMode) => {
    setPendingTheme(newMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    // Close modal immediately
    setShowThemeModal(false);
    
    // Update local state and storage
    setIsDarkMode(pendingTheme);
    localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
    
    try {
      // Update database in background
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData && userData.id) {
            const response = await authFetch(`${API_BASE_URL}/api/update-theme`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user_id: userData.id,
                is_darkmode: pendingTheme ? 1 : 0
              })
            });
            
            if (!response.ok) {
              console.error('Failed to update theme in database');
            }
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    // Preserve theme before clearing storage
    const currentTheme = localStorage.getItem('theme');
    
    // Clear all stored authentication data
    localStorage.clear();
    sessionStorage.clear();
    
    // Restore theme
    if (currentTheme) {
      localStorage.setItem('theme', currentTheme);
    }
    
    setShowLogoutModal(false);
    // Use replace to prevent back button navigation
    navigate('/login-superuser', { replace: true });
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.profile-section')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className={`${styles.systemApp} ${isDarkMode ? '' : styles.lightMode}`}>
      {/* System Header */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.systemInfo}>
            <div className={styles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={styles.systemUser}>{formatDate(currentTime)}</div>
          </div>
          <div className={`${styles.profileSection} profile-section`}>
            <div className={styles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={styles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={styles.dropdownDivider}></div>
                <button className={styles.dropdownItem} onClick={() => toggleDarkMode(!isDarkMode)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={styles.dropdownItem} onClick={() => setShowChangePasswordModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <button className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Dashboard Layout */}
      <div className={styles.dashboardContainer}>
        {/* Sidebar Navigation */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarTitle}>Main Modules</div>
            <ul className={styles.sidebarMenu}>
              <li className={styles.sidebarItem}>
                <button type="button" className={`${styles.sidebarLink} ${styles.active}`}>
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Dashboard
                </button>
              </li>
            </ul>
          </div>
          
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarTitle}>Search & Reports</div>
            <ul className={styles.sidebarMenu}>
              <li className={styles.sidebarItem}>
                <button type="button" className={styles.sidebarLink} onClick={() => navigate('/su-jo-search-health-record')}>
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="11.5" cy="14.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M13.3 16.3L15 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Search Health Records
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button type="button" className={styles.sidebarLink} onClick={() => navigate('/su-jo-search-consultation')}>
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M16 4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4M16 4C16 5.10457 15.1046 6 14 6H10C8.89543 6 8 5.10457 8 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="11" cy="14" r="2" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M12.5 15.5L14 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Search Consultations
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button type="button" className={styles.sidebarLink} onClick={() => navigate('/su-final-remarks-search')}>
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M16 4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4M16 4C16 5.10457 15.1046 6 14 6H10C8.89543 6 8 5.10457 8 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="11" cy="14" r="2" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M12.5 15.5L14 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Search Final Remarks
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button type="button" className={styles.sidebarLink} onClick={() => navigate('/su-filter-health')}>
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Filter PDL
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button type="button" className={styles.sidebarLink} onClick={() => navigate('/su-pdl-cell')}>
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 15H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  PDL Cell
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button type="button" className={styles.sidebarLink} onClick={() => navigate('/su-activity-logs')}>
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                    <path d="M3.05 11H1M5.2 5.2L3.8 3.8M12 1V3.05M18.8 5.2L20.2 3.8M21 12H22.95M18.8 18.8L20.2 20.2M12 22.95V21M5.2 18.8L3.8 20.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Activity Logs
                </button>
              </li>
            </ul>
          </div>

          {/* Administration section removed as requested */}
        </aside>

        {/* Main Content Area */}
        <main className={styles.mainContent}>
          {/* Key Performance Indicators */}
          <div className={styles.kpiGrid}>
            {loading ? (
              <>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <div key={index} className={styles.skeletonKpiCard}>
                    <div className={styles.skeletonKpiHeader}>
                      <div className={styles.skeletonIcon}></div>
                      <div className={styles.skeletonTrend}></div>
                    </div>
                    <div className={styles.skeletonValue}></div>
                    <div className={styles.skeletonLabel}></div>
                    <div className={styles.skeletonSublabel}></div>
                    <div className={styles.skeletonChart}></div>
                  </div>
                ))}
              </>
            ) : (
              <>
            <div 
              className={`${styles.kpiCard} ${styles.secondary}`}
              onClick={() => navigate('/su-jo-search-health-record', { state: { autoLoad: true } })}
              style={{cursor: 'pointer'}}
            >
              <div className={styles.kpiHeader}>
                <div className={styles.kpiIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2V22M17 7H7M17 17H7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <div className={styles.kpiContent}>
                <div className={styles.kpiValue}>
                  {loading ? '...' : 
                    analyticsData.healthRecordsCount?.data?.total || 
                    analyticsData.kpis?.healthRecords?.value || 
                    0
                  }
                </div>
                <div className={styles.kpiLabel}>Health Records</div>
                <div className={styles.kpiSublabel}>
                  {loading ? 'Loading...' : 
                    analyticsData.healthRecordsCount?.data ? 
                      `Total records in hr_info table (${analyticsData.healthRecordsCount.data.thisMonth} this month)` :
                      'Medical assessments completed'
                  }
                </div>
              </div>
              <div className={styles.kpiChart}>
                <div className={styles.miniChart}>
                  <div className={styles.chartLine} style={{background: 'linear-gradient(90deg, rgba(16,185,129,0.3) 0%, rgba(16,185,129,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div 
              className={`${styles.kpiCard} ${styles.quaternary}`}
              onClick={() => navigate('/su-jo-search-consultation', { state: { autoLoad: true } })}
              style={{cursor: 'pointer'}}
            >
              <div className={styles.kpiHeader}>
                <div className={styles.kpiIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className={styles.kpiContent}>
                <div className={styles.kpiValue}>
                  {loading ? '...' : 
                    analyticsData.dailyConsultationCount?.data?.total || 
                    analyticsData.kpis?.dailyConsultations?.value || 
                    0
                  }
                </div>
                <div className={styles.kpiLabel}>Consultation</div>
                <div className={styles.kpiSublabel}>
                  {loading ? 'Loading...' : 
                    analyticsData.dailyConsultationCount?.data ? 
                      `Total consultations in health_consult table (${analyticsData.dailyConsultationCount.data.today} today)` :
                      'Consultations completed today'
                  }
                </div>
              </div>
              <div className={styles.kpiChart}>
                <div className={styles.miniChart}>
                  <div className={styles.chartLine} style={{background: 'linear-gradient(90deg, rgba(245,158,11,0.3) 0%, rgba(245,158,11,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div 
              className={`${styles.kpiCard} ${styles.tertiary}`}
              onClick={() => navigate('/su-filter-health', { state: { filterType: 'recommendation', recommendationValue: 'dorm' } })}
              style={{cursor: 'pointer'}}
            >
              <div className={styles.kpiHeader}>
                <div className={styles.kpiIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className={styles.kpiContent}>
                <div className={styles.kpiValue}>
                  {loading ? '...' : 
                    analyticsData.generalDormitoryCount?.data?.total || 
                    analyticsData.kpis?.generalDormitory?.value || 
                    0
                  }
                </div>
                <div className={styles.kpiLabel}>General Dormitory</div>
                <div className={styles.kpiSublabel}>
                  {loading ? 'Loading...' : 
                    analyticsData.generalDormitoryCount?.data ? 
                      `Total dorm recommendations (${analyticsData.generalDormitoryCount.data.thisMonth} this month)` :
                      'Inmates in general housing'
                  }
                </div>
              </div>
              <div className={styles.kpiChart}>
                <div className={styles.miniChart}>
                  <div className={styles.chartLine} style={{background: 'linear-gradient(90deg, rgba(139,92,246,0.3) 0%, rgba(139,92,246,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div 
              className={`${styles.kpiCard} ${styles.quinary}`}
              onClick={() => navigate('/su-filter-health', { state: { filterType: 'recommendation', recommendationValue: 'isolated' } })}
              style={{cursor: 'pointer'}}
            >
              <div className={styles.kpiHeader}>
                <div className={styles.kpiIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className={styles.kpiContent}>
                <div className={styles.kpiValue}>
                  {loading ? '...' : 
                    analyticsData.isolationCount?.data?.total || 
                    analyticsData.kpis?.isolation?.value || 
                    0
                  }
                </div>
                <div className={styles.kpiLabel}>Isolation</div>
                <div className={styles.kpiSublabel}>
                  {loading ? 'Loading...' : 
                    analyticsData.isolationCount?.data ? 
                      `Total isolation recommendations (${analyticsData.isolationCount.data.thisMonth} this month)` :
                      'Inmates in isolation cells'
                  }
                </div>
              </div>
              <div className={styles.kpiChart}>
                <div className={styles.miniChart}>
                  <div className={styles.chartLine} style={{background: 'linear-gradient(90deg, rgba(239,68,68,0.3) 0%, rgba(239,68,68,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div 
              className={`${styles.kpiCard} ${styles.senary}`}
              onClick={() => navigate('/su-filter-health', { state: { filterType: 'recommendation', recommendationValue: 'hospitalized' } })}
              style={{cursor: 'pointer'}}
            >
              <div className={styles.kpiHeader}>
                <div className={styles.kpiIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2V22M17 7H7M17 17H7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                    <path d="M20 9V15C20 15.5304 19.7893 16.0391 19.4142 16.4142C19.0391 16.7893 18.5304 17 18 17H6C5.46957 17 4.96086 16.7893 4.58579 16.4142C4.21071 16.0391 4 15.5304 4 15V9C4 8.46957 4.21071 7.96086 4.58579 7.58579C4.96086 7.21071 5.46957 7 6 7H18C18.5304 7 19.0391 7.21071 19.4142 7.58579C19.7893 7.96086 20 8.46957 20 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className={styles.kpiContent}>
                <div className={styles.kpiValue}>
                  {loading ? '...' : 
                    analyticsData.hospitalCount?.data?.total || 
                    analyticsData.kpis?.hospital?.value || 
                    0
                  }
                </div>
                <div className={styles.kpiLabel}>Hospital</div>
                <div className={styles.kpiSublabel}>
                  {loading ? 'Loading...' : 
                    analyticsData.hospitalCount?.data ? 
                      `Total hospital recommendations (${analyticsData.hospitalCount.data.thisMonth} this month)` :
                      'Inmates in medical facility'
                  }
                </div>
              </div>
              <div className={styles.kpiChart}>
                <div className={styles.miniChart}>
                  <div className={styles.chartLine} style={{background: 'linear-gradient(90deg, rgba(6,182,212,0.3) 0%, rgba(6,182,212,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div 
              className={`${styles.kpiCard} ${styles.unassigned}`}
              onClick={() => navigate('/su-pdl-cell', { state: { autoLoad: true } })}
              style={{cursor: 'pointer'}}
            >
              <div className={styles.kpiHeader}>
                <div className={styles.kpiIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className={styles.kpiContent}>
                <div className={styles.kpiValue}>
                  {loading ? '...' : 
                    analyticsData.unassignedPdlCount?.data?.total || 
                    analyticsData.kpis?.unassignedPdls?.value || 
                    0
                  }
                </div>
                <div className={styles.kpiLabel}>Unassigned PDLs</div>
                <div className={styles.kpiSublabel}>
                  {loading ? 'Loading...' : 
                    analyticsData.unassignedPdlCount?.data ? 
                      `Total PDLs without assigned cell (${analyticsData.unassignedPdlCount.data.thisMonth} this month)` :
                      'PDLs without cell assignment'
                  }
                </div>
              </div>
              <div className={styles.kpiChart}>
                <div className={styles.miniChart}>
                  <div className={styles.chartLine} style={{background: 'linear-gradient(90deg, rgba(236,72,153,0.3) 0%, rgba(236,72,153,1) 100%)'}}></div>
                </div>
              </div>
            </div>
              </>
            )}
          </div>

          {/* Analytics Dashboard Grid */}
          <div className={styles.analyticsGrid}>
            {/* Age Demographics */}
            <div className={`${styles.analyticsCard} ${styles.mediumChart}`} onClick={() => navigate('/su-filter-health', { state: { filterType: 'age' } })} style={{cursor: 'pointer'}}>
              <div className={styles.cardHeader}>
                <h3>Age Distribution</h3>
                <p className={styles.chartSubtitle}>Inmate age demographics</p>
              </div>
              <div className={`${styles.chartContainer} ${styles.medium}`}>
                {loading ? (
                  <div className="skeleton-analytics-content">
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                  </div>
                ) : (
                <div className={styles.demographicsChart}>
                  <div className={styles.ageDistribution}>
                    <div className={styles.ageBars}>
                      {(() => {
                        const allGroups = ['0-17', '18-25', '26-35', '36-45', '46+'];
                        const groupMap = {};
                        (analyticsData.ageDistribution?.ageGroups || []).forEach(g => { groupMap[g.label] = g; });
                        return allGroups.map(label => {
                          const g = groupMap[label] || { label, count: 0, percentage: 0 };
                          const cssKey = label.replace(/-/g, '').replace('+', 'Plus');
                          return (
                            <div key={g.label} className={styles.ageBar}>
                              <div className={styles.barInfo}>
                                <span className={styles.ageLabel}>{g.label}</span>
                                <span className={styles.ageValue}>{g.count}</span>
                              </div>
                              <div className={styles.barContainer}>
                                <div
                                  className={`${styles.barFill} ${styles[`age${cssKey}`]}`}
                                  style={{width: `${g.percentage}%`}}
                                ></div>
                              </div>
                              <span className={styles.barPercentage}>{g.percentage}%</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Gender Demographics */}
            <div className={`${styles.analyticsCard} ${styles.mediumChart}`} onClick={() => navigate('/su-filter-health', { state: { filterType: 'sex' } })} style={{cursor: 'pointer'}}>
              <div className={styles.cardHeader}>
                <h3>Gender Distribution</h3>
                <p className={styles.chartSubtitle}>Male vs Female breakdown</p>
              </div>
              <div className={`${styles.chartContainer} ${styles.medium}`}>
                {loading ? (
                  <div className="skeleton-analytics-content">
                    <div className="skeleton-gender-chart">
                      <div className="skeleton-pie-chart"></div>
                      <div className="skeleton-gender-stats">
                        <div className="skeleton-stat-row"></div>
                        <div className="skeleton-stat-row"></div>
                        <div className="skeleton-stat-row"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                <div className={styles.demographicsChart}>
                  <div className={styles.genderDistribution}>
                    <div className={styles.genderVisual}>
                      <div className={styles.genderArcWrapper}>
                        <div className={styles.genderArc}>
                          <div 
                            className={`${styles.arcSegment} ${styles.male}`} 
                            style={{'--percentage': analyticsData.genderDistribution?.distribution?.male?.percentage || 78}}
                          ></div>
                          <div 
                            className={`${styles.arcSegment} ${styles.female}`} 
                            style={{'--percentage': analyticsData.genderDistribution?.distribution?.female?.percentage || 22}}
                          ></div>
                        </div>
                        <div className={styles.genderCenterLegend}>
                          <div className={styles.genderLegendItem}>
                            <span className={`${styles.genderLegendDot} ${styles.male}`}></span>
                            <span className={styles.genderLegendLabel}>Male</span>
                          </div>
                          <div className={styles.genderLegendItem}>
                            <span className={`${styles.genderLegendDot} ${styles.female}`}></span>
                            <span className={styles.genderLegendLabel}>Female</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.genderStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total Male</span>
                        <span className={`${styles.statValue} ${styles.male}`}>
                          {analyticsData.genderDistribution?.distribution?.male?.count || 563}
                        </span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total Female</span>
                        <span className={`${styles.statValue} ${styles.female}`}>
                          {analyticsData.genderDistribution?.distribution?.female?.count || 159}
                        </span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total Inmates</span>
                        <span className={`${styles.statValue} ${styles.total}`}>
                          {analyticsData.genderDistribution?.total || 722}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Cell Distribution */}
            <div className={`${styles.analyticsCard} ${styles.mediumChart}`} onClick={() => navigate('/su-pdl-cell')} style={{cursor: 'pointer'}}>
              <div className={styles.cardHeader}>
                <h3>Cell Distribution</h3>
                <p className={styles.chartSubtitle}>Inmate distribution across all cells</p>
              </div>
              <div className={`${styles.chartContainer} ${styles.medium}`}>
                {loading ? (
                  <div className="skeleton-analytics-content">
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                  </div>
                ) : (
                  <CellDistributionCardSU cellData={analyticsData.cellDistribution} styles={styles} />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={JSON.parse(localStorage.getItem('user'))}
        onSuccess={(msg) => {
          setSuccessMessage(msg);
          setShowSuccessModal(true);
        }}
        onError={(msg) => {
          setErrorMessage(msg);
          setShowErrorModal(true);
        }}
      />

      {/* Success Modal */}
      {showSuccessModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Success</h3>
            <p>{successMessage}</p>
            <button className={styles.successBtn} onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Error Modal */}
      {showErrorModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Theme Change Confirmation Modal */}
      {showThemeModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={cancelThemeChange}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#f59e0b' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{ color: '#f59e0b' }}>Change Theme</h3>
            <p>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelThemeChange}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmThemeChange} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>Confirm</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={cancelLogout}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,17 21,12 16,7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelLogout}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Cell Distribution Component with Pagination for SU Homepage
function CellDistributionCardSU({ cellData, styles }) {
  const [currentPage, setCurrentPage] = React.useState(0);
  const ITEMS_PER_PAGE = 4;
  
  if (!cellData?.data?.allCells || cellData.data.allCells.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
        No cell data available
      </div>
    );
  }

  const allCells = cellData.data.allCells;
  const totalPages = Math.ceil(allCells.length / ITEMS_PER_PAGE) + 1; // +1 for summary page
  const isOnSummaryPage = currentPage === 0;
  
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentCells = isOnSummaryPage ? [] : allCells.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className={styles.demographicsChart}>
      <div className={styles.ageDistribution}>
        {isOnSummaryPage ? (
          // Summary page
          cellData.data.summary && (
            <div className={`${styles.genderStats} ${styles.cellSummaryStats}`}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Total Cells</span>
                <span className={`${styles.statValue} ${styles.total}`}>
                  {cellData.data.summary.totalCells}
                </span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Occupancy</span>
                <span className={`${styles.statValue} ${styles.male}`}>
                  {cellData.data.summary.totalOccupancy}/{cellData.data.summary.totalCapacity}
                </span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Occupancy Rate</span>
                <span className={`${styles.statValue} ${cellData.data.summary.occupancyRate >= 90 ? styles.female : styles.male}`}>
                  {cellData.data.summary.occupancyRate}%
                </span>
              </div>
            </div>
          )
        ) : (
          // Cell distribution pages
          <div className={styles.ageBars}>
            {currentCells.map((cell) => (
              <div key={cell.id} className={styles.ageBar}>
                <div className={styles.barInfo}>
                  <span className={styles.ageLabel}>{cell.type} - {cell.cellNo}</span>
                  <span className={styles.ageValue}>{cell.occupancy}/{cell.capacity}</span>
                </div>
                <div className={styles.barContainer}>
                  <div 
                    className={`${styles.barFill} ${
                      cell.status === 'full' ? styles.age46Plus : 
                      cell.status === 'near_full' ? styles.age3645 : 
                      styles.age1825
                    }`}
                    style={{width: `${Math.max(cell.occupancyRate, 2)}%`}}
                  ></div>
                </div>
                <span className={styles.barPercentage}>{cell.occupancyRate}%</span>
              </div>
            ))}
          </div>
        )}
        {totalPages > 1 && (
          <div className={styles.cellPagination}>
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className={styles.cellPaginationBtn}
            >
              Previous
            </button>
            <span className={styles.cellPaginationInfo}>
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              className={styles.cellPaginationBtn}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SuHomepage;
